import { useNavigate, useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { BOARD_CONFIG, BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { AnnounceBanner } from "@/features/announce/components/AnnounceBanner";
import { DesktopAnnouncementContent } from "@/features/announce/components/DesktopAnnouncementContent";
import { DesktopCatalogGridLayout } from "@/features/catalog/components/desktop/DesktopCatalogGridLayout";
import { DesktopCatalogItem } from "@/features/catalog/components/desktop/DesktopCatalogItem";
import { CatalogItem } from "@/features/catalog/components/lists/CatalogItem";
import { MobileCatalogGridLayout } from "@/features/catalog/components/lists/MobileCatalogGridLayout";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import {
  DesktopHistorySidebar,
  type DesktopHistorySidebarHandle,
} from "@/features/history/components/DesktopHistorySidebar";
import { DesktopHistoryToolbar } from "@/features/history/components/DesktopHistoryToolbar";
import { HistoryModeToggle } from "@/features/history/components/HistoryModeToggle";
import { historyThreadKey } from "@/features/history/historyIdentity";
import {
  type HistoryMode,
  historyModeLabel,
  parseHistoryMode,
} from "@/features/history/historyMode";
import {
  selectHistoryItemsForScope,
  useHistorySummaryScope,
} from "@/features/history/hooks/historySummaryScope";
import { useHistoryThreads } from "@/features/history/hooks/useHistoryThreads";
import { useHistoryStore } from "@/features/history/stores/historyStore";
import {
  selectActivePostedItems,
  usePostedHistoryStore,
} from "@/features/history/stores/postedHistoryStore";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useIsDesktop } from "@/shared/hooks/useMediaQuery";
import { hasLinkModifierKey } from "@/shared/lib/keyboardAccessibility";
import { BmgBanner } from "@/shared/ui/ad";
import { LoadingScreen, Message } from "@/shared/ui/feedback";

export const HistoryPage: React.FunctionComponent = () => {
  const { mode: modeParam } = useParams({ from: "/history/$mode" });
  const mode = parseHistoryMode(modeParam);
  const navigate = useNavigate({ from: "/history/$mode" });
  const viewed = useHistoryStore((state) => state.viewed);
  const posted = usePostedHistoryStore((state) => state.posted);
  const historyScope = useHistorySummaryScope();
  const historyItems = selectHistoryItemsForScope(
    mode === "posted" ? selectActivePostedItems(posted) : viewed,
    historyScope,
  );
  const query = useHistoryThreads(mode, historyItems, historyScope);
  const isDesktop = useIsDesktop();
  const contentTitle = historyModeLabel(mode);
  useDocumentTitle(`${contentTitle} - ${BOARD_DISPLAY_NAME}`);
  const columns = useCatalogStore((state) => state.columns);
  const imageSize = useCatalogStore((state) => state.imageSize);
  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [prevIsDesktop, setPrevIsDesktop] = useState(isDesktop);
  if (isDesktop !== prevIsDesktop) {
    setPrevIsDesktop(isDesktop);
    if (!isDesktop) setIsSettingsOpen(false);
  }

  const historyContent =
    historyItems.length === 0 ? (
      <Message variant="info">{contentTitle}はありません</Message>
    ) : query.isLoading ? (
      <LoadingScreen />
    ) : query.error ? (
      <Message variant="error">{contentTitle}の読み込みに失敗しました</Message>
    ) : query.data?.length === 0 ? (
      <Message variant="info">現在存在するスレッドはありません</Message>
    ) : null;

  if (isDesktop) {
    return (
      <section
        aria-labelledby="history-page-title"
        className="desktop-catalog-page"
      >
        <DesktopHistoryToolbar
          mode={mode}
          isFetching={query.isFetching}
          onRefresh={(): void => void query.refetch()}
          onToggleSidebar={(): void =>
            desktopHistorySidebarRef.current?.toggle()
          }
          onScrollToTop={(): void => window.scrollTo({ top: 0 })}
          onScrollToBottom={(): void =>
            window.scrollTo({ top: document.documentElement.scrollHeight })
          }
          onSettingsClick={(
            event: React.MouseEvent<HTMLAnchorElement>,
          ): void => {
            if (hasLinkModifierKey(event)) return;
            event.preventDefault();
            setIsSettingsOpen(true);
          }}
        />
        <h1 id="history-page-title" className="desktop-mode-title">
          履歴
        </h1>
        <DesktopAnnouncementContent>
          <section
            className="desktop-catalog-banner"
            aria-label="お知らせと広告"
          >
            <AnnounceBanner />
            <BmgBanner reserveEmptySpace priority />
          </section>
          <HistoryModeToggle
            mode={mode}
            onChange={(nextMode: HistoryMode): void => {
              void navigate({
                to: "/history/$mode",
                params: { mode: nextMode },
              });
            }}
            className="desktop-history-mode-toggle"
          />
          {historyContent ?? (
            <DesktopCatalogGridLayout columns={columns} imageSize={imageSize}>
              {query.data?.map((thread) => (
                <DesktopCatalogItem
                  key={historyThreadKey({
                    boardSlug: thread.b ?? BOARD_CONFIG.boardKey,
                    threadId: thread.id,
                  })}
                  thread={thread}
                  isNew={false}
                />
              ))}
            </DesktopCatalogGridLayout>
          )}
        </DesktopAnnouncementContent>
        <DesktopHistorySidebar
          ref={desktopHistorySidebarRef}
          showClosedHandle={false}
        />
        <DesktopSettingsModal
          isOpen={isSettingsOpen}
          onClose={(): void => setIsSettingsOpen(false)}
        />
      </section>
    );
  }

  return (
    <>
      <AnnounceBanner />
      <BmgBanner reserveEmptySpace priority />
      <section aria-labelledby="history-page-title" className="history-page">
        <h1 id="history-page-title" className="sr-only">
          履歴
        </h1>
        <HistoryModeToggle
          mode={mode}
          onChange={(nextMode: HistoryMode): void => {
            void navigate({
              to: "/history/$mode",
              params: { mode: nextMode },
            });
          }}
        />
        {historyContent ? (
          <section className="py-3" aria-label={contentTitle}>
            {historyContent}
          </section>
        ) : (
          <MobileCatalogGridLayout columns={columns}>
            {query.data?.map((thread) => (
              <CatalogItem
                key={historyThreadKey({
                  boardSlug: thread.b ?? BOARD_CONFIG.boardKey,
                  threadId: thread.id,
                })}
                thread={thread}
                isNew={false}
              />
            ))}
          </MobileCatalogGridLayout>
        )}
      </section>
    </>
  );
};
